/**
 * Data plane — Sources (L0, ADR-0026).
 *
 * Lists every registered connection: the seeded internal source (Tray's own
 * Postgres, non-deletable) + N external DBs. Per-row Test probes connectivity
 * and Esplora drills the live schema. The internal source can be tested and
 * explored but not edited or deleted.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Database,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  TestTube2,
  Trash2,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { SourceDialog, type DialogMode } from "@/components/sources/SourceDialog";
import { SourceExploreDialog } from "@/components/sources/SourceExploreDialog";
import { SourceKindChooser } from "@/components/sources/SourceKindChooser";
import { SourceUploadDialog } from "@/components/sources/SourceUploadDialog";
import { TagList } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DescriptionTooltip } from "@/components/ui/description-tooltip";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { sourcesApi, type Source } from "@/lib/api-sources";
import { usePermissions } from "@/lib/permissions";

export function SourcesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("sources", "write");
  const canDelete = can("sources", "delete");
  const canManage = canWrite || canDelete;

  const [dialog, setDialog] = React.useState<{ open: boolean; mode: DialogMode }>({
    open: false,
    mode: { kind: "create" },
  });
  const [exploreTarget, setExploreTarget] = React.useState<Source | null>(null);
  const [chooserOpen, setChooserOpen] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [reuploadTarget, setReuploadTarget] = React.useState<Source | null>(null);

  const listQ = useQuery({ queryKey: ["sources"], queryFn: () => sourcesApi.list() });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      sourcesApi.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
  });

  const remove = useMutation({
    mutationFn: sourcesApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sources"] }),
  });

  const [testTarget, setTestTarget] = React.useState<string | null>(null);
  const test = useMutation({ mutationFn: sourcesApi.test });

  const rows = listQ.data ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("sources.title", "Sorgenti")}
        description={t(
          "sources.subtitle",
          "Connessioni registrate ai dati — il Postgres interno di Tray e i database esterni. Sola lettura.",
        )}
        actions={
          canWrite && (
            <Button size="sm" onClick={() => setChooserOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("sources.new", "Nuova sorgente")}
            </Button>
          )
        }
      />
      <PageContent>
        {rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={Database}
            title={t("sources.empty", "Nessuna sorgente")}
            description={t(
              "sources.emptyDescription",
              "Collega un database esterno (Postgres, MySQL, SQL Server) per renderlo una sorgente interrogabile.",
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-12">{t("sources.col.on", "On")}</Th>
                  <Th>{t("sources.col.name", "Nome")}</Th>
                  <Th className="w-24">{t("sources.col.kind", "Tipo")}</Th>
                  <Th>{t("sources.col.tags", "Tag")}</Th>
                  <Th>{t("sources.col.lastUsed", "Ultimo uso")}</Th>
                  {canManage && <Th className="w-44 text-right">{t("sources.col.actions", "Azioni")}</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((s) => (
                  <React.Fragment key={s.id}>
                    <Tr>
                      <Td onClick={(e) => e.stopPropagation()}>
                        {canWrite && !s.is_system ? (
                          <Switch
                            checked={s.enabled}
                            onCheckedChange={(v) => toggle.mutate({ id: s.id, enabled: v })}
                          />
                        ) : (
                          <Badge tone={s.enabled ? "active" : "neutral"}>
                            {s.enabled ? "on" : "off"}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <div className="flex items-center gap-2 font-medium">
                          {s.name}
                          {s.is_system && (
                            <Badge tone="neutral" className="text-[10px]">
                              {t("sources.systemBadge", "sistema")}
                            </Badge>
                          )}
                          <DescriptionTooltip text={s.description} />
                        </div>
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1.5">
                          <Badge tone={s.kind === "internal" ? "active" : "neutral"} className="text-[10px]">
                            {s.kind === "internal"
                              ? t("sources.kind.internal", "interna")
                              : s.kind === "file"
                                ? t("sources.kind.file", "file")
                                : t("sources.kind.external", "esterna")}
                          </Badge>
                          {s.kind === "file" && typeof s.config?.row_count === "number" && (
                            <span className="text-[11px] text-muted-foreground">
                              {t("sources.fileRows", "{{count}} righe", { count: s.config.row_count as number })}
                            </span>
                          )}
                          {s.kind === "external" && s.health_status !== "unknown" && (
                            <Badge
                              tone={s.health_status === "ok" ? "active" : "danger"}
                              className="text-[10px]"
                              title={
                                s.health_checked_at
                                  ? t("sources.health.checkedAt", "Ultimo controllo: {{when}}", {
                                      when: new Date(s.health_checked_at).toLocaleString(),
                                    })
                                  : undefined
                              }
                            >
                              {s.health_status === "ok"
                                ? t("sources.health.ok", "online")
                                : t("sources.health.down", "offline")}
                            </Badge>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <TagList tags={s.tags} max={3} />
                      </Td>
                      <Td className="text-xs text-muted-foreground">
                        {s.last_used_at ? new Date(s.last_used_at).toLocaleString() : "—"}
                      </Td>
                      {canManage && (
                        <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t("sources.action.test", "Test")}
                              title={t("sources.action.test", "Testa la connessione")}
                              onClick={() => {
                                setTestTarget(s.id);
                                test.mutate(s.id);
                              }}
                            >
                              <TestTube2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t("sources.action.explore", "Esplora")}
                              title={t("sources.action.explore", "Esplora schemi e tabelle")}
                              onClick={() => setExploreTarget(s)}
                            >
                              <Search className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                            {canWrite && s.kind === "file" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("sources.action.reupload", "Re-importa il file")}
                                title={t("sources.action.reupload", "Re-importa il file")}
                                onClick={() => setReuploadTarget(s)}
                              >
                                <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canWrite && !s.is_system && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("common.edit", "Modifica")}
                                onClick={() => setDialog({ open: true, mode: { kind: "edit", source: s } })}
                              >
                                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && !s.is_system && (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={t("common.delete", "Elimina")}
                                onClick={() => {
                                  if (confirm(t("sources.deleteConfirm", `Eliminare la sorgente "${s.name}"?`)))
                                    remove.mutate(s.id);
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </Td>
                      )}
                    </Tr>
                    {testTarget === s.id && test.data && (
                      <Tr>
                        <Td colSpan={canManage ? 6 : 5} className="!py-0">
                          <Alert
                            tone={test.data.ok ? "info" : "danger"}
                            title={
                              test.data.ok
                                ? t("sources.test.ok", "Connessione riuscita")
                                : t("sources.test.failed", "Connessione fallita")
                            }
                            className="my-2"
                          >
                            <div className="flex items-start gap-2 font-mono text-xs">
                              {test.data.ok ? (
                                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" strokeWidth={1.5} />
                              ) : (
                                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" strokeWidth={1.5} />
                              )}
                              <span>{test.data.message}</span>
                            </div>
                          </Alert>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>

      <SourceDialog
        open={dialog.open}
        mode={dialog.mode}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
      />
      <SourceExploreDialog
        source={exploreTarget}
        onOpenChange={(open) => !open && setExploreTarget(null)}
      />
      <SourceKindChooser
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        onPick={(choice) => {
          setChooserOpen(false);
          if (choice === "file") setUploadOpen(true);
          else setDialog({ open: true, mode: { kind: "create" } });
        }}
      />
      <SourceUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
      <SourceUploadDialog
        open={!!reuploadTarget}
        reuploadSource={reuploadTarget}
        onOpenChange={(open) => !open && setReuploadTarget(null)}
      />
    </PageContainer>
  );
}
